// Effectivity timeline bar (Module 01 §4, used on S2/S3/S4). A horizontal band
// of version segments ordered oldest→newest, each showing its version number,
// status and effective date range. Structural typing means BOM/routing version
// summaries (BomSummary / RoutingSummary) can be passed directly.
//
// SCHEDULED (spec 01 §9.16, D-261). Given `today`, an `active` segment whose
// `effective_from` is still ahead reads "Scheduled" (amber) instead of
// "Active" — the wire status is unchanged, the human meaning is not. Without
// `today` the bar renders the wire status as before, so a caller that has no
// business date in hand never paints a wrong one.

import { useTranslation } from "react-i18next";
import { StatusBadge } from "../../../../components/status-badge";
import { StatusChip } from "../../../../components/status-chip";
import { isScheduledVersion } from "./version-schedule";

export interface EffectivityVersion {
  id: string;
  version: number;
  status: string;
  effective_from: string | null;
  effective_to: string | null;
}

export function EffectivityTimeline({
  versions,
  selectedId,
  onSelect,
  today,
}: {
  versions: readonly EffectivityVersion[];
  selectedId?: string | undefined;
  onSelect?: ((id: string) => void) | undefined;
  /** The business date "scheduled" is judged against (facility-local, D-089). */
  today?: string | undefined;
}) {
  const { t } = useTranslation();

  if (versions.length === 0) {
    return <p className="effectivity-empty">{t("catalogue.effectivity.none")}</p>;
  }

  const ordered = [...versions].sort((a, b) => a.version - b.version);

  return (
    <div className="effectivity-timeline" aria-label={t("catalogue.effectivity.title")}>
      {ordered.map((v) => {
        const selected = v.id === selectedId;
        const scheduled =
          today !== undefined && isScheduledVersion(v.status, v.effective_from, today);
        const range =
          v.effective_from === null
            ? "—"
            : `${v.effective_from} → ${v.effective_to ?? t("catalogue.effectivity.open")}`;
        const tone = scheduled ? "scheduled" : v.status;
        const className = `effectivity-segment status-tone-${tone}${selected ? " is-selected" : ""}`;
        const content = (
          <>
            <span className="effectivity-version">v{v.version}</span>
            {scheduled ? (
              <StatusChip variant="warn" data-status="scheduled">
                {t("catalogue.status.scheduled")}
              </StatusChip>
            ) : (
              <StatusBadge status={v.status} />
            )}
            <span className="effectivity-range">{range}</span>
          </>
        );
        return onSelect !== undefined ? (
          <button
            type="button"
            className={className}
            key={v.id}
            aria-pressed={selected}
            onClick={() => {
              onSelect(v.id);
            }}
          >
            {content}
          </button>
        ) : (
          <div className={className} key={v.id}>
            {content}
          </div>
        );
      })}
    </div>
  );
}
